import { cn } from "@u/cn";
import Button from "@ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ui/select";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface DashboardPaginationProps {
  page: number;
  pageSize: number;
  total: number;
  pageSizeOptions?: number[];
  onPage(page: number): void;
  onPageSize(size: number): void;
  className?: string;
}

export function DashboardPagination({
  page,
  pageSize,
  total,
  pageSizeOptions = [10, 25, 50, 100],
  onPage,
  onPageSize,
  className,
}: DashboardPaginationProps) {
  const { t } = useTranslation();
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const smallest = Math.min(...pageSizeOptions);
  if (total <= smallest && pageCount <= 1) return null;

  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(total, page * pageSize);

  return (
    <div
      className={cn(
        "flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 py-4 border-t border-black/5 dark:border-white/5",
        className,
      )}
    >
      <div className="flex items-center gap-3">
        <p className="label-nano text-muted-foreground">
          {t("ds_range_of_total", {
            start,
            end,
            total,
            defaultValue: "{{start}}–{{end}} of {{total}}",
          })}
        </p>
        <Select
          value={String(pageSize)}
          onValueChange={(v) => onPageSize(Number(v))}
        >
          <SelectTrigger
            size="sm"
            aria-label={t("ds_page_size", { defaultValue: "Rows per page" })}
            className="w-auto"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent position="popper">
            {pageSizeOptions.map((size) => (
              <SelectItem key={size} value={String(size)}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2">
        <Button
          design="outline"
          size="icon"
          icon={ChevronLeft}
          aria-label={t("ds_prev_page", { defaultValue: "Previous page" })}
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          className="rounded-xl h-11 w-11 sm:h-8 sm:w-8"
        />
        <p className="label-nano text-muted-foreground">
          {t("ds_page_of", {
            page,
            pageCount,
            defaultValue: "{{page}} / {{pageCount}}",
          })}
        </p>
        <Button
          design="outline"
          size="icon"
          icon={ChevronRight}
          aria-label={t("ds_next_page", { defaultValue: "Next page" })}
          disabled={page >= pageCount}
          onClick={() => onPage(page + 1)}
          className="rounded-xl h-11 w-11 sm:h-8 sm:w-8"
        />
      </div>
    </div>
  );
}
